import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useI18n } from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || '';

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function ChargersList({ filters, onToggleFavorite, favoriteChargers = [] }) {
  const { t } = useI18n();
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [distance, setDistance] = useState(25);
  const [chargers, setChargers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'distance', direction: 'asc' });
  const [hoveredCharger, setHoveredCharger] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  
  const debounceRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Fetch municipality suggestions
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/municipios/search?q=${encodeURIComponent(query)}&limit=8`);
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setSuggestions(data.data);
        setShowSuggestions(true);
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    }
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedMunicipality(null);
    
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
  };

  const handleSelectSuggestion = (municipality) => {
    setSearchTerm(municipality.displayName || municipality.name);
    setSelectedMunicipality(municipality);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const searchChargers = async () => {
    if (!selectedMunicipality) {
      setError(t('chargers.selectCity'));
      return;
    }
    
    setLoading(true);
    setError(null);
    setSearched(true);
    
    try {
      const params = new URLSearchParams({
        municipality: selectedMunicipality.name,
        distance: distance.toString(),
      });
      if (filters?.currentTypes?.length) params.set('currentTypes', filters.currentTypes.join(','));
      if (filters?.connectors?.length) params.set('connectors', filters.connectors.join(','));
      if (filters?.powerRanges?.length) params.set('powerRanges', filters.powerRanges.join(','));
      
      const response = await fetch(`${API_URL}/api/chargers/by-municipality?${params}`);
      const data = await response.json();
      
      if (data.success) {
        // Enriquecer con distancia calculada desde el centro de búsqueda
        const centerLat = selectedMunicipality.coordinates?.lat || selectedMunicipality.lat;
        const centerLng = selectedMunicipality.coordinates?.lng || selectedMunicipality.lng;
        const enriched = (data.data || []).map(c => {
          const cLat = c.coordinates?.lat || c.lat;
          const cLng = c.coordinates?.lng || c.lng;
          const distKm = (cLat && cLng && centerLat && centerLng)
            ? Math.round(haversineKm(centerLat, centerLng, cLat, cLng) * 10) / 10
            : null;
          return { ...c, distanceKm: distKm };
        });
        setChargers(enriched);
      } else {
        setChargers([]);
      }
    } catch (err) {
      console.error('Error fetching chargers:', err);
      setChargers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAllChargers = async () => {
    setLoading(true);
    setError(null);
    setSearched(true);
    setSearchTerm('');
    setSelectedMunicipality(null);
    
    try {
      const params = new URLSearchParams();
      if (filters?.currentTypes?.length) params.set('currentTypes', filters.currentTypes.join(','));
      if (filters?.connectors?.length) params.set('connectors', filters.connectors.join(','));
      if (filters?.powerRanges?.length) params.set('powerRanges', filters.powerRanges.join(','));
      const url = `${API_URL}/api/chargers/all?${params}`;
        
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        setChargers((data.data || []).map(c => ({ ...c, distanceKm: null })));
      } else {
        setChargers([]);
      }
    } catch (err) {
      console.error('Error fetching all chargers:', err);
      setChargers([]);
    } finally {
      setLoading(false);
    }
  };

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getSortableValue = useCallback((charger, key) => {
    switch (key) {
      case 'operator':
        return (charger.operator || 'Desconocido').toLowerCase();
      case 'city':
        return (charger.municipioInfo?.nombre || charger.address?.city || '').toLowerCase();
      case 'province':
        return (charger.municipioInfo?.provincia || charger.address?.province || '').toLowerCase();
      case 'address': {
        const addressValue = charger.address
          ? (typeof charger.address === 'string' ? charger.address : charger.address.street || '')
          : '';
        return addressValue.toLowerCase();
      }
      case 'connectors':
        return (charger.connectors?.map(c => c.type).join(', ') || '').toLowerCase();
      case 'maxPower':
        return Number(charger.maxPower || 0);
      case 'price':
        return (charger.usageCost || '').toLowerCase();
      case 'distance':
        return charger.distanceKm != null ? charger.distanceKm : 99999;
      default:
        return '';
    }
  }, []);

  const sortedChargers = useMemo(() => {
    if (!sortConfig.key) return chargers;

    const sorted = [...chargers].sort((a, b) => {
      const aValue = getSortableValue(a, sortConfig.key);
      const bValue = getSortableValue(b, sortConfig.key);

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return aValue - bValue;
      }

      return String(aValue).localeCompare(String(bValue), 'es', { sensitivity: 'base' });
    });

    return sortConfig.direction === 'asc' ? sorted : sorted.reverse();
  }, [chargers, sortConfig, getSortableValue]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const handleRowMouseEnter = (e, charger) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({ x: Math.min(rect.right + 10, window.innerWidth - 300), y: Math.max(rect.top, 10) });
    setHoveredCharger(charger);
  };

  return (
    <div className="chargers-search-panel">
      <h3>⚡ {t('chargers.title')}</h3>
      
      <div className="chargers-search-form">
        <div className="search-field" ref={suggestionsRef}>
          <label>{t('chargers.cityLabel')}</label>
          <input
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            placeholder={t('chargers.cityPlaceholder')}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
          />
          {showSuggestions && suggestions.length > 0 && (
            <ul className="municipality-suggestions">
              {suggestions.map((m, idx) => (
                <li key={idx} onClick={() => handleSelectSuggestion(m)}>
                  {m.displayName || m.name}
                </li>
              ))}
            </ul>
          )}
        </div>
        
        <div className="distance-field">
          <label>{t('chargers.radiusLabel')}</label>
          <input
            type="number"
            value={distance}
            onChange={(e) => setDistance(Number(e.target.value) || 25)}
            min="1"
            max="100"
          />
        </div>
        
        <button 
          onClick={searchChargers} 
          disabled={loading || !selectedMunicipality}
          className="search-chargers-btn"
        >
          {loading ? t('chargers.searching') : t('chargers.search')}
        </button>
        
        <button 
          onClick={loadAllChargers} 
          disabled={loading}
          className="view-all-chargers-btn"
        >
          {loading ? t('chargers.loading') : t('chargers.viewAll')}
        </button>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {searched && !loading && (
        <div className="chargers-results">
          {chargers.length > 0 ? (
            <>
              <p className="results-count">{t('chargers.found', { count: chargers.length })}</p>
              <div className="chargers-table-container">
                <table className="chargers-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('distance')} style={{ cursor: 'pointer' }}>📏 {t('chargers.distance')} {getSortIndicator('distance')}</th>
                      <th onClick={() => handleSort('operator')} style={{ cursor: 'pointer' }}>{t('chargers.operator')} {getSortIndicator('operator')}</th>
                      <th onClick={() => handleSort('maxPower')} style={{ cursor: 'pointer' }}>{t('chargers.maxPower')} {getSortIndicator('maxPower')}</th>
                      <th onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}>{t('chargers.price')} {getSortIndicator('price')}</th>
                      <th onClick={() => handleSort('connectors')} style={{ cursor: 'pointer' }}>{t('chargers.connectors')} {getSortIndicator('connectors')}</th>
                      <th onClick={() => handleSort('city')} style={{ cursor: 'pointer' }}>{t('chargers.city')} {getSortIndicator('city')}</th>
                      <th onClick={() => handleSort('address')} style={{ cursor: 'pointer' }}>{t('chargers.address')} {getSortIndicator('address')}</th>
                      <th style={{ width: 36, textAlign: 'center' }}>⭐</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedChargers.map((charger, idx) => {
                      const isFav = favoriteChargers.some(f => f.id === charger.id);
                      return (
                        <tr
                          key={charger.id || idx}
                          onMouseEnter={(e) => handleRowMouseEnter(e, charger)}
                          onMouseLeave={() => setHoveredCharger(null)}
                        >
                          <td className="distance-cell">
                            {charger.distanceKm != null ? `${charger.distanceKm} km` : '-'}
                          </td>
                          <td><strong>{charger.operator || t('chargers.unknown')}</strong></td>
                          <td className="power-cell">{charger.maxPower ? `${charger.maxPower} kW` : '-'}</td>
                          <td className="price-cell" style={{ color: '#059669', fontWeight: 500 }}>
                            {charger.usageCost || '€--/kWh'}
                            {charger.usageCost && (
                              <span
                                title={t('chargers.priceDisclaimer')}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 13, height: 13, borderRadius: '50%', background: '#e5e7eb', color: '#6b7280', fontSize: '0.65em', fontWeight: 'bold', cursor: 'help', marginLeft: 3, verticalAlign: 'middle' }}
                              >i</span>
                            )}
                          </td>
                          <td>{charger.connectors?.map(c => c.type).join(', ') || '-'}</td>
                          <td className="secondary-cell">{charger.municipioInfo?.nombre || charger.address?.city || '-'}</td>
                          <td className="secondary-cell">
                            {charger.address 
                              ? (typeof charger.address === 'string' 
                                  ? charger.address 
                                  : charger.address.street || '-')
                              : '-'}
                          </td>
                          <td style={{ textAlign: 'center', width: 36 }}>
                            {onToggleFavorite && (
                              <button
                                className={`fav-star-btn${isFav ? ' active' : ''}`}
                                onClick={() => onToggleFavorite(charger)}
                                title={isFav ? t('chargers.unfavorite') : t('chargers.favorite')}
                              >
                                {isFav ? '★' : '☆'}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="no-results">
              {selectedMunicipality 
                ? t('chargers.noResultsIn', { city: selectedMunicipality.name, distance })
                : t('chargers.noResults')}
            </p>
          )}
        </div>
      )}

      {/* Tooltip flotante al pasar por encima de un cargador */}
      {hoveredCharger && (
        <div
          className="charger-hover-tooltip"
          style={{ position: 'fixed', left: tooltipPos.x, top: tooltipPos.y, zIndex: 9999 }}
        >
          <strong>{hoveredCharger.operator || t('chargers.unknown')}</strong>
          {hoveredCharger.distanceKm != null && (
            <div className="tooltip-distance">📏 {hoveredCharger.distanceKm} km</div>
          )}
          {hoveredCharger.maxPower && <div className="tooltip-power">⚡ {hoveredCharger.maxPower} kW</div>}
          <div className="tooltip-price" style={{ color: '#059669', fontWeight: 500 }}>💰 {hoveredCharger.usageCost || '€--/kWh'}</div>
          <div className="tooltip-connectors">{hoveredCharger.connectors?.map(c => c.type).join(', ') || '-'}</div>
          <div className="tooltip-address">
            {hoveredCharger.address
              ? (typeof hoveredCharger.address === 'string'
                ? hoveredCharger.address
                : [hoveredCharger.address.street, hoveredCharger.address.city].filter(Boolean).join(', '))
              : '-'}
          </div>
          {hoveredCharger.municipioInfo?.nombre && (
            <div className="tooltip-municipality">{hoveredCharger.municipioInfo.nombre}, {hoveredCharger.municipioInfo.provincia}</div>
          )}
        </div>
      )}
    </div>
  );
}
